import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FileUploadComponent, UploadedFile } from 'components/input/file-upload/file-upload';
import { EditRowComponent } from 'components/layout/data-table/edit-row.component';
import { UserComponent } from 'components/layout/user/user.component';
import { ViewTaskflowComponent } from 'components/task/taskflow/view-taskflow.component';
import { FormattedContentDirective } from 'directives/formatted-content.directive';
import { InputType } from 'enums/InputType';
import { TaskActionType } from 'enums/TaskActionType';
import { TaskActionModel } from 'models/generated/Entities.GormCrm.TaskAction';
import { TaskAreaModel } from 'models/generated/Entities.GormCrm.TaskArea';
import { InputTypeDateTimeModel } from 'models/generated/Models.InputTypeData.InputTypeDateTime';
import { TaskActionInquiryModel } from 'models/generated/Models.TaskActionAdditionalData.TaskActionInquiry';
import { SelectListItemModel } from 'models/SelectList';
import { TranslatePipe } from 'pipes/translate.pipe';
import { NavigationService } from 'services/navigation.service';
import { TaskService } from 'services/task.service';
import { UserService } from 'services/user.service';
import { ProductLaunchViewComponent } from './product-launch-view.component';
import { ButtonComponent } from 'components/input/button/button.component';
import { FileReferenceModel } from 'models/generated/Entities.GormCrm.FileReference';
import { FileStorageService } from 'services/file-storage.service';
import { Layout } from 'modules/layout';
import { Pipes } from 'modules/pipes';
import { SingleFocusService } from 'components/input/editable-content/editable-content.component';

@Component({
	selector: 'view-task-action',
	templateUrl: './view-task-action.component.html',
	imports: [Layout, ProductLaunchViewComponent, EditRowComponent, UserComponent, FormattedContentDirective, FileUploadComponent, Pipes],
	providers: [SingleFocusService]
})
export class ViewTaskActionComponent {

	private _taskAction = new TaskActionModel();
	private taskAreas?: TaskAreaModel[];
	taskPlanedDateTime: string | undefined;
	additionalDataValid = true;
	usersList: SelectListItemModel[];
	taskAreaList: SelectListItemModel[] = [];
	taskActionTypeEnum = TaskActionType;
	inputTypeEnum = InputType;
	inquiryModel = new TaskActionInquiryModel();

	@ViewChild('createCorrectionButton') createCorrectionButton?: ButtonComponent;

	@Input() blockCompletion = false;

	@Output() createNewTaskAction = new EventEmitter<TaskActionModel>();

	@Input()
	set taskAction(action: TaskActionModel) {
		this._taskAction = action;
		this.additionalDataValid = true;
		this.uploadedFiles = [];
		this.uploadUrl = '/api/upload/create-task-attachments/' + this.taskAction.id!;
		if (this.taskAction.plannedDate) {
			this.taskPlanedDateTime = JSON.stringify({ date: new Date(this.taskAction.plannedDate!), dateOnly: this.taskAction.dateOnly });
		}

		if (action.type === this.taskActionTypeEnum.inquiry && action.additionalData) this.inquiryModel = Object.assign(this.inquiryModel, JSON.parse(action.additionalData));
	}
	get taskAction() {
		return this._taskAction;
	}
	uploading = false;
	uploadedFiles: UploadedFile[] = [];
	uploadUrl = '/api/upload/create-task-attachments';
	constructor(
		userService: UserService,
		translate: TranslatePipe,
		private readonly fileStorageService: FileStorageService,
		private readonly taskService: TaskService,
		private readonly navigation: NavigationService) {

		this.usersList = userService.getUserSelectList();
		taskService.getAllTaskAreas().then(taskAreas => {
			this.taskAreas = taskAreas;
			for (let area of taskAreas) {
				this.taskAreaList.push(new SelectListItemModel(area.id!, translate.transform('task-area.area-name.' + area.name)));
			}
		});
	}

	canComplete() {
		if (this.blockCompletion) return false;
		if (this.uploading) return false;
		if (!this.additionalDataValid) return false;
		return true;
	}
	updateTaskArea(taskAreaId: string) {
		this.taskAction.taskArea = this.taskAreas!.find(ta => ta.id === taskAreaId);
		this.taskService.updateTaskActionArea(this.taskAction.id!, taskAreaId);
	}
	updateUser(userId: string) {
		this.taskAction.assignedToUserId = userId;
		this.taskService.updateTaskActionAssignedTo(this.taskAction.id!, userId);
	}
	updateDetails(details: string) {
		this.taskService.updateTaskActionDetails(this.taskAction.id!, details).then(() => this.taskAction.details = details);
	}

	updatePlannedDate(dateTimeData?: string) {
		var dateTime = new InputTypeDateTimeModel();
		if (dateTimeData) dateTime = JSON.parse(dateTimeData) as InputTypeDateTimeModel;
		this.taskService.updateTaskActionPlannedDate(this.taskAction.id!, dateTime.date!, dateTime.dateOnly);
	}
	closeTaskAction() {
		this.taskService.closeTaskAction(this.taskAction.id!, 'task closed', undefined).then(() => {

			if (this.taskAction.type == TaskActionType.qualificationCall) {
				this.taskService.getTaskflow(this.taskAction.taskflowId!).then(tf => {
					var productLaunchTask = tf.taskActions.find(ta => ta.step == tf.currentStep && ta.type == TaskActionType.productLaunch);
					if (productLaunchTask) {
						this.navigation.refreshView(ViewTaskflowComponent).then(view => {
							view.taskflowId = this.taskAction.taskflowId!;
							view.selectedActionId = productLaunchTask!.id;
						});
					}
				});
			}
		});
	}
	cloneTaskflowAndCloseTaskAction() {
		this.taskService.cloneTaskflowAndCloseTaskAction(this.taskAction.taskflowId!, this.taskAction.id!).then((taskflow) => {
			if (!taskflow.id)
				return;
			this.navigation.refreshView(ViewTaskflowComponent).then(view => {
				view.taskflowId = taskflow.id!;
			});
		});
	}
	showCloneTaskflowAndCloseTaskActionButton(): boolean {
		return this.taskAction.completed == null && this.taskAction.type === TaskActionType.presentation;
	}

	createCorrectionTaskAction() {
		const newTaskAction = new TaskActionModel();
		newTaskAction.type = TaskActionType.correction;
		newTaskAction.step = this.taskAction.step;
		if (this.taskAction.taskflow && this.taskAction.taskflow.taskActions.length > 0) {
			const designerTaskActions = this.taskAction.taskflow.taskActions.filter(taskAction => taskAction.type === TaskActionType.design);
			if (designerTaskActions.length > 0) {
				newTaskAction.assignedToUserId = designerTaskActions[0].assignedToUserId;
			}
		}
		this.createNewTaskAction.emit(newTaskAction);
		if (this.createCorrectionButton) this.createCorrectionButton.reset();
	}
	showCreateCorrectionTaskActionButton(): boolean {
		return this.taskAction.completed == null && this.taskAction.type === TaskActionType.presentation;
	}

	updateInquiryReply(reply: string) {
		const oldValue = this.inquiryModel.reply;
		this.inquiryModel.reply = reply;
		this.taskService.updateTaskActionInquiryReply(this.taskAction.id!, reply)
			.then(() => {
				this.taskAction.AdditionalData = JSON.stringify(this.inquiryModel);
			})
			.catch(() => {
				this.inquiryModel.reply = oldValue;
			});
	}

	downloadFile(file?: FileReferenceModel) {
		if (file) this.fileStorageService.downloadFile(file.filePath);
	}
}
